import {
  AlertOutlined,
  AppstoreOutlined,
  ArrowRightOutlined,
  BarChartOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ClusterOutlined,
  DollarOutlined,
  FileProtectOutlined,
  OrderedListOutlined,
  RocketOutlined,
  SyncOutlined,
  TeamOutlined,
  TruckOutlined
} from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography
} from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { transportApi } from "../../api/transportApi";
import { useIsAdmin } from "../../shared/hooks/usePermission";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

// ─── Status colour maps ───────────────────────────────────────────────────────
const CONSIGNMENT_STATUS_COLOR: Record<string, string> = {
  Draft: "default", Booked: "blue", Planned: "cyan", Loaded: "geekblue",
  InTransit: "orange", Arrived: "lime", OutForDelivery: "gold",
  Delivered: "green", Cancelled: "red", Returned: "volcano"
};

const TRIP_STATUS_COLOR: Record<string, string> = {
  Planned: "blue", Started: "orange", InTransit: "gold",
  Completed: "green", Cancelled: "red"
};

// ─── Metric card ─────────────────────────────────────────────────────────────
interface MetricCardProps {
  title: string;
  value: number;
  suffix?: string;
  icon: React.ReactNode;
  color: string;
  sub?: string;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, suffix, icon, color, sub, onClick }) => (
  <Card
    className="panel-card"
    hoverable={!!onClick}
    onClick={onClick}
    style={{ cursor: onClick ? "pointer" : "default" }}
    styles={{ body: { padding: "18px 20px" } }}
  >
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div>
        <Text type="secondary" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {title}
        </Text>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
          <span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{value.toLocaleString()}</span>
          {suffix && <Text type="secondary" style={{ fontSize: 13 }}>{suffix}</Text>}
        </div>
        {sub && <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>{sub}</Text>}
      </div>
      <div
        style={{
          width: 44, height: 44, borderRadius: 10,
          background: `${color}18`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, color
        }}
      >
        {icon}
      </div>
    </div>
  </Card>
);

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, action }: { icon: React.ReactNode; title: string; action?: React.ReactNode }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
    <Space>
      {icon}
      <Text strong style={{ fontSize: 14 }}>{title}</Text>
    </Space>
    {action}
  </div>
);

// ─── Main dashboard ───────────────────────────────────────────────────────────
export const DashboardPage = () => {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    transportApi.getDashboardStats()
      .then(setStats)
      .catch((e) => setError(e?.response?.data?.message ?? "Failed to load dashboard data"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="panel-card" style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" tip="Loading dashboard..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="panel-card">
        <Alert
          type="error"
          showIcon
          message="Dashboard failed to load"
          description={error}
          action={<Button onClick={() => window.location.reload()}>Retry</Button>}
        />
      </Card>
    );
  }

  const c = stats?.consignments ?? {};
  const m = stats?.manifests ?? {};
  const t = stats?.trips ?? {};
  const v = stats?.vehicles ?? {};
  const d = stats?.drivers ?? {};
  const e = stats?.ewayBills ?? {};
  const recentConsignments: any[] = stats?.recentConsignments ?? [];
  const recentTrips: any[] = stats?.recentTrips ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Page title ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Operations Dashboard</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {dayjs().format("dddd, DD MMMM YYYY")} · Live overview
          </Text>
        </div>
        <Space>
          <Button icon={<BarChartOutlined />} onClick={() => navigate("/reports")}>Reports</Button>
          {isAdmin && (
            <Button type="primary" icon={<AppstoreOutlined />} onClick={() => navigate("/consignments")}>
              New Booking
            </Button>
          )}
        </Space>
      </div>

      {/* ── KPI row 1: Consignments + Manifests + Trips ── */}
      <Row gutter={[14, 14]}>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Total Consignments"
            value={c.total ?? 0}
            icon={<AppstoreOutlined />}
            color="#1677ff"
            sub={`${c.active ?? 0} active · ${c.today ?? 0} today`}
            onClick={() => navigate("/consignments")}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Manifests"
            value={m.total ?? 0}
            icon={<OrderedListOutlined />}
            color="#722ed1"
            sub={`${m.thisMonth ?? 0} this month`}
            onClick={() => navigate("/manifests")}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Active Trips"
            value={t.active ?? 0}
            icon={<RocketOutlined />}
            color="#fa8c16"
            sub={`${t.planned ?? 0} planned · ${t.completed ?? 0} completed`}
            onClick={() => navigate("/trips")}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="E-Way Bills"
            value={e.active ?? 0}
            suffix="active"
            icon={<FileProtectOutlined />}
            color="#52c41a"
            sub={`${e.total ?? 0} total`}
            onClick={() => navigate("/ewaybill")}
          />
        </Col>
      </Row>

      {/* ── KPI row 2: Fleet + Drivers + Parties + LorryHire ── */}
      <Row gutter={[14, 14]}>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Fleet"
            value={v.active ?? 0}
            suffix="active"
            icon={<TruckOutlined />}
            color="#1677ff"
            sub={`${v.total ?? 0} total vehicles`}
            onClick={() => navigate("/fleet")}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Drivers"
            value={d.active ?? 0}
            suffix="active"
            icon={<TeamOutlined />}
            color="#eb2f96"
            sub={`${d.total ?? 0} registered`}
            onClick={() => navigate("/drivers")}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Parties"
            value={stats?.partyCount ?? 0}
            icon={<ClusterOutlined />}
            color="#13c2c2"
            sub="Customers & vendors"
            onClick={() => navigate("/parties")}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <MetricCard
            title="Lorry Hire"
            value={stats?.lorryHire?.total ?? 0}
            icon={<CarOutlined />}
            color="#faad14"
            sub={`${stats?.lorryHire?.thisMonth ?? 0} this month`}
            onClick={() => navigate("/lorry-hire")}
          />
        </Col>
      </Row>

      {/* ── Trips breakdown + Recent consignments ── */}
      <Row gutter={[14, 14]}>
        {/* Active trips */}
        <Col xs={24} lg={10}>
          <Card className="panel-card" styles={{ body: { padding: "16px 20px" } }}>
            <SectionHeader
              icon={<RocketOutlined style={{ color: "#fa8c16" }} />}
              title="Active & Planned Trips"
              action={
                <Button size="small" type="link" icon={<ArrowRightOutlined />} onClick={() => navigate("/trips")}>
                  All trips
                </Button>
              }
            />
            {recentTrips.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px 0", color: "#999" }}>
                <TruckOutlined style={{ fontSize: 28, marginBottom: 8 }} />
                <div style={{ fontSize: 13 }}>No active trips right now</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {recentTrips.map((trip: any) => (
                  <div
                    key={trip.id}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: "var(--app-bg, #f9f9f9)",
                      border: "1px solid var(--app-bg, #f0f0f0)"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Space size={6}>
                        <Badge
                          color={trip.status === "InTransit" || trip.status === "Started" ? "#fa8c16" : "#1677ff"}
                          dot
                        />
                        <Text strong style={{ fontSize: 13 }}>
                          {trip.origin} → {trip.destination}
                        </Text>
                      </Space>
                      <Tag color={TRIP_STATUS_COLOR[trip.status] ?? "default"} style={{ margin: 0 }}>
                        {trip.status}
                      </Tag>
                    </div>
                    <div style={{ marginTop: 4, paddingLeft: 14 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {trip.vehicleNo ?? "—"} · {trip.driverName ?? "No driver"} ·{" "}
                        {trip.startDate ? dayjs(trip.startDate).fromNow() : ""}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* Recent consignments */}
        <Col xs={24} lg={14}>
          <Card className="panel-card" styles={{ body: { padding: "16px 20px" } }}>
            <SectionHeader
              icon={<AppstoreOutlined style={{ color: "#1677ff" }} />}
              title="Recent Consignments"
              action={
                <Button size="small" type="link" icon={<ArrowRightOutlined />} onClick={() => navigate("/consignments")}>
                  All bookings
                </Button>
              }
            />
            <Table
              dataSource={recentConsignments}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                {
                  title: "LR No",
                  dataIndex: "consignmentNo",
                  width: 110,
                  render: (v: string) => <Text strong style={{ fontSize: 12 }}>{v || "—"}</Text>
                },
                {
                  title: "Route",
                  key: "route",
                  render: (_: any, row: any) => (
                    <Text style={{ fontSize: 12 }}>{row.origin} → {row.destination}</Text>
                  )
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  width: 110,
                  render: (s: string) => (
                    <Tag color={CONSIGNMENT_STATUS_COLOR[s] ?? "default"} style={{ fontSize: 11, margin: 0 }}>{s}</Tag>
                  )
                },
                {
                  title: "Date",
                  dataIndex: "bookingDate",
                  width: 90,
                  render: (d: string) => (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(d).format("DD MMM")}
                    </Text>
                  )
                }
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Operations summary + Accounting placeholder ── */}
      <Row gutter={[14, 14]}>
        {/* Operations quick actions */}
        <Col xs={24} lg={10}>
          <Card className="panel-card" styles={{ body: { padding: "16px 20px" } }}>
            <SectionHeader
              icon={<ClusterOutlined style={{ color: "#722ed1" }} />}
              title="Quick Actions"
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                { label: "Book New Consignment", icon: <AppstoreOutlined />, path: "/consignments", color: "#1677ff" },
                { label: "Create Lorry Manifest", icon: <OrderedListOutlined />, path: "/manifests", color: "#722ed1" },
                { label: "Start a Trip", icon: <RocketOutlined />, path: "/trips", color: "#fa8c16" },
                { label: "Register E-Way Bill", icon: <FileProtectOutlined />, path: "/ewaybill", color: "#52c41a" },
                { label: "View Reports", icon: <BarChartOutlined />, path: "/reports", color: "#13c2c2" },
                { label: "Document Vault", icon: <AlertOutlined />, path: "/attachments", color: "#eb2f96" }
              ].map(({ label, icon, path, color }) => (
                <div
                  key={path}
                  onClick={() => navigate(path)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 12px",
                    borderRadius: 8,
                    background: "var(--app-bg, #f9f9f9)",
                    border: "1px solid var(--app-bg, #f0f0f0)",
                    cursor: "pointer",
                    transition: "all 0.15s"
                  }}
                >
                  <span style={{ color, fontSize: 16 }}>{icon}</span>
                  <Text style={{ fontSize: 13, flex: 1 }}>{label}</Text>
                  <ArrowRightOutlined style={{ color: "#ccc", fontSize: 11 }} />
                </div>
              ))}
            </div>
          </Card>
        </Col>

        {/* Accounting panel — pluggable placeholder */}
        <Col xs={24} lg={14}>
          <Card
            className="panel-card"
            styles={{ body: { padding: "16px 20px" } }}
          >
            <SectionHeader
              icon={<DollarOutlined style={{ color: "#52c41a" }} />}
              title="Accounting Overview"
              action={
                <Button size="small" type="link" icon={<ArrowRightOutlined />} onClick={() => navigate("/accounting")}>
                  Open accounting
                </Button>
              }
            />

            <Alert
              type="info"
              showIcon
              icon={<SyncOutlined />}
              message="Accounting module integration pending"
              description="The accounting system is designed as a pluggable module. Financial summaries, P&L, ledger balances, and commission reports will appear here once the accounting integration is activated for your organisation."
              style={{ marginBottom: 16 }}
            />

            {/* Preview accounting metrics — will be live once integrated */}
            <Row gutter={[12, 12]}>
              {[
                { label: "Revenue (MTD)", value: "—", icon: <DollarOutlined />, color: "#52c41a", tooltip: "Monthly revenue from freight charges" },
                { label: "Payables", value: "—", icon: <ClockCircleOutlined />, color: "#fa8c16", tooltip: "Outstanding lorry hire & vendor payments" },
                { label: "Commission Due", value: "—", icon: <BarChartOutlined />, color: "#722ed1", tooltip: "Pending commission payouts" },
                { label: "P&L (MTD)", value: "—", icon: <CheckCircleOutlined />, color: "#1677ff", tooltip: "Month-to-date profit & loss" }
              ].map(({ label, value, icon, color, tooltip }) => (
                <Col key={label} span={12}>
                  <Tooltip placement="bottom" title={tooltip}>
                    <div
                      style={{
                        padding: "12px 14px",
                        borderRadius: 8,
                        background: `${color}08`,
                        border: `1px solid ${color}22`,
                        display: "flex",
                        alignItems: "center",
                        gap: 10
                      }}
                    >
                      <span style={{ color, fontSize: 18 }}>{icon}</span>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
                        <div style={{ fontWeight: 700, fontSize: 16, color: "#999" }}>{value}</div>
                      </div>
                    </div>
                  </Tooltip>
                </Col>
              ))}
            </Row>

            <Divider style={{ margin: "14px 0 12px" }} />

            <Space>
              <Button size="small" onClick={() => navigate("/accounting")}>Finance & Accounting</Button>
              <Button size="small" onClick={() => navigate("/commission")}>Commission</Button>
              <Button size="small" onClick={() => navigate("/lorry-hire")}>Lorry Hire Payables</Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
